/** Client-only Canvas viewport persistence (refresh restore).
 * (VISTOERA_CANVAS_SPLASH_AMBIENT_LANGUAGE_AND_VIEWPORT_RESTORE_V3)
 *
 * localStorage only — no network, no database. Keyed per workspace + project. */

import type { CanvasViewportDto } from './types'

export const CANVAS_VIEWPORT_MIN_ZOOM = 0.1
export const CANVAS_VIEWPORT_MAX_ZOOM = 2
export const CANVAS_VIEWPORT_PERSIST_DEBOUNCE_MS = 500

export interface PersistedCanvasViewport extends CanvasViewportDto {
  updatedAt: number
}

export function canvasViewportStorageKey(workspaceId: string, projectId: string): string {
  return `vistoera.canvas.viewport.${workspaceId}.${projectId}`
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value)
}

export function normalizeCanvasViewport(
  input: Partial<CanvasViewportDto> | null | undefined,
  minZoom = CANVAS_VIEWPORT_MIN_ZOOM,
  maxZoom = CANVAS_VIEWPORT_MAX_ZOOM,
): CanvasViewportDto | null {
  if (!input) return null
  const { x, y, zoom } = input
  if (!isFiniteNumber(x) || !isFiniteNumber(y) || !isFiniteNumber(zoom)) return null
  if (zoom < minZoom || zoom > maxZoom) return null
  return { x, y, zoom }
}

export function readPersistedCanvasViewport(
  workspaceId: string,
  projectId: string,
): CanvasViewportDto | null {
  if (!workspaceId || !projectId || typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(canvasViewportStorageKey(workspaceId, projectId))
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<PersistedCanvasViewport>
    return normalizeCanvasViewport(parsed)
  } catch {
    return null
  }
}

export function writePersistedCanvasViewport(
  workspaceId: string,
  projectId: string,
  viewport: CanvasViewportDto,
): void {
  if (!workspaceId || !projectId || typeof window === 'undefined') return
  const normalized = normalizeCanvasViewport(viewport)
  if (!normalized) return
  try {
    const payload: PersistedCanvasViewport = {
      ...normalized,
      updatedAt: Date.now(),
    }
    window.localStorage.setItem(canvasViewportStorageKey(workspaceId, projectId), JSON.stringify(payload))
  } catch {
    /* Quota / privacy mode — ignore silently. */
  }
}
